import { useState } from "react";
import { Text, Stack, Button } from "@mantine/core";
import { IconCheck, IconCurrencyDollar } from "@tabler/icons-react";
import EmailReceiptForm from "./EmailReceiptForm";
import TipModal from "../calendar/CalendarView/components/TransactionDetailModal/TipModal";
import { useApp } from "../../context/AppContext";
import {
  GetTransactionsResponse,
  TransactionResponse,
} from "../../types/transactions";
import { ProcessCardPaymentResponse } from "../../types/payments";

export type CardPaymentTips = ProcessCardPaymentResponse["tips"];

interface CardPaymentSuccessPanelProps {
  paymentIntentId: string;
  customerEmail?: string | null;
  transaction: GetTransactionsResponse | TransactionResponse;
  backendTips: CardPaymentTips | null;
  onTipsChanged?: () => void;
}

function getUniqueStaffCount(tips: CardPaymentTips | null | undefined): number {
  if (!tips) return 0;
  return Object.keys(tips).length;
}

export default function CardPaymentSuccessPanel({
  paymentIntentId,
  customerEmail,
  transaction,
  backendTips,
  onTipsChanged,
}: CardPaymentSuccessPanelProps) {
  const { editTransactionLineItems } = useApp();
  const [isTipModalOpen, setIsTipModalOpen] = useState(false);

  const handleTipSubmit = async (tipData: {
    line_items: { booked_services: Array<{ id: string; tip?: number }> };
  }) => {
    const bsTipsByIds =
      tipData.line_items.booked_services?.reduce(
        (acc: Record<string, number>, service) => {
          acc[service.id] = service.tip || 0;
          return acc;
        },
        {},
      ) || {};

    const finalTipData = {
      transaction_id: transaction.transaction.id,
      line_items: {
        booked_services: transaction.line_items.booked_services.map(
          (service) => ({
            id: service.id,
            line_item_id: service.line_item_id,
            tip: bsTipsByIds[service.id] || 0,
          }),
        ),
      },
    };

    await editTransactionLineItems(finalTipData);
    onTipsChanged?.();
    setIsTipModalOpen(false);
  };

  return (
    <>
      <Text size="lg" ta="center">
        All set! Your payment has been successfully processed.
      </Text>

      <Stack align="center" gap="md">
        <IconCheck
          size={60}
          color="limegreen"
          style={{
            padding: "12px",
            borderRadius: "100%",
            border: "2px solid limegreen",
          }}
        />
      </Stack>

      <EmailReceiptForm
        paymentIntentId={paymentIntentId}
        customerEmail={customerEmail}
      />

      {getUniqueStaffCount(backendTips) > 1 && (
        <Button
          onClick={() => setIsTipModalOpen(true)}
          fullWidth
          leftSection={<IconCurrencyDollar size={16} />}
          variant="light"
          color="green"
          mt="md"
        >
          Allocate Tips
        </Button>
      )}

      <TipModal
        opened={isTipModalOpen}
        onClose={() => setIsTipModalOpen(false)}
        transaction={transaction}
        onSubmit={handleTipSubmit}
        backendTips={backendTips}
      />
    </>
  );
}
